// Steps/ConfirmPurchaseStep.jsx
// Shows the chosen number and a wallet-billing note, then buys it via
// /telephonenumberaccount/purchase. IMPORTANT: no Contract_Id is sent — omitting
// the contract is what routes monthly rental to the System Wallet (a backend
// cron bills it). On success we store the purchased number and advance.

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Label, Input, FormGroup, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  telephoneNumberPurchase,
  telephoneNumberPurchaseSuccess,
} from "../../../../store/thirdPartyIntegration/action";

export function ConfirmPurchaseStep({ wizard }) {
  const {
    dispatch,
    callProviderId,
    country,
    selectedNumber,
    purchasedNumber,
    setPurchasedNumber,
    nickname,
    setNickname,
    numberType,
    goNext,
    goBack,
  } = wizard;

  const { purchaseDataAPI, purchaseLoading } = useSelector((state) => ({
    purchaseDataAPI: state.ThirdPartyIntegration.telephoneNumberPurchaseData,
    purchaseLoading:
      state.ThirdPartyIntegration.telephoneNumberPurchaseLoading,
  }));

  useEffect(() => {
    if (isEmpty(purchaseDataAPI)) return;
    if (purchaseDataAPI.status === 200) {
      toast.success(purchaseDataAPI?.message || "Number purchased");
      setPurchasedNumber(
        purchaseDataAPI?.TelephoneNumber || selectedNumber || ""
      );
      dispatch(telephoneNumberPurchaseSuccess([]));
      goNext();
    } else {
      toast.error(
        purchaseDataAPI?.error ||
          purchaseDataAPI?.message ||
          "Purchase failed"
      );
      dispatch(telephoneNumberPurchaseSuccess([]));
    }
  }, [purchaseDataAPI]);

  const handleBuy = () => {
    if (!selectedNumber) {
      toast.error("Pick a number first");
      return;
    }
    // No Contract_Id → billed to the System Wallet by the backend cron.
    dispatch(
      telephoneNumberPurchase({
        CallProvider_Id: callProviderId,
        CountryCode: country || "GB",
        TelephoneNumber: selectedNumber,
        Nickname: nickname || undefined,
        // Change #100774 - only sent for mobile (backward compatible).
        ...(numberType === "mobile" ? { NumberType: "mobile" } : {}),
      })
    );
  };

  const alreadyBought = !!purchasedNumber;

  return (
    <div className="step-container">
      <div className="mb-4 pb-2 border-bottom">
        <h4 className="text-pastel-blue fw-bold mb-1">Confirm &amp; buy</h4>
        <p className="text-muted small mb-0">
          Review the number below before purchasing.
        </p>
      </div>

      <Card className="border-0 shadow-sm rounded-4 bg-white p-4 mb-3">
        <span className="fw-semibold d-block text-muted small text-uppercase mb-1">
          Selected number
        </span>
        <div className="fs-4 fw-bold text-dark mb-3">
          {selectedNumber || purchasedNumber || "-"}
        </div>

        <FormGroup>
          <Label for="tel-nickname">Nickname (optional)</Label>
          <Input
            id="tel-nickname"
            type="text"
            value={nickname}
            placeholder="e.g. Bristol office line"
            onChange={(e) => setNickname(e.target.value)}
            disabled={alreadyBought}
          />
        </FormGroup>

        <div className="alert alert-info d-flex align-items-center mb-0 mt-2">
          <i className="ri-wallet-3-line fs-5 me-2"></i>
          <span>
            Monthly rental for this number is billed to your{" "}
            <strong>System Wallet</strong>.
          </span>
        </div>
      </Card>

      <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-light rounded-pill px-4 fw-semibold text-muted"
          onClick={goBack}
          disabled={purchaseLoading}
        >
          <i className="ri-arrow-left-line me-2"></i> Back
        </button>

        {alreadyBought ? (
          <button
            type="button"
            className="btn btn-primary rounded-pill px-4 fw-semibold"
            onClick={goNext}
          >
            Next <i className="ri-arrow-right-line ms-2"></i>
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-success rounded-pill px-4 fw-semibold"
            onClick={handleBuy}
            disabled={purchaseLoading || !selectedNumber}
          >
            {purchaseLoading ? (
              <>
                <Spinner size="sm" className="me-2" /> Buying…
              </>
            ) : (
              <>
                <i className="ri-shopping-cart-2-line me-2"></i> Buy this number
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

ConfirmPurchaseStep.propTypes = {
  wizard: PropTypes.object.isRequired,
};
